<template>
    <div class="costs-container">
        <h2 class="page-title">인프라 운영 비용</h2>
        <p class="page-sub">
            개별 서버로 구성하는 경우와 저희 서버에 통합 운영하는 경우의 월 비용 비교입니다.
        </p>

        <!-- 비교: 개별 서버 구성 vs 통합 인프라 운영 -->
        <div class="compare">
            <div class="compare-card">
                <div class="compare-label">개별 서버 구성 시</div>
                <div class="compare-usd">{{ usd(individual) }}<span class="per">/월</span></div>
            </div>
            <div class="compare-arrow">→</div>
            <div class="compare-card primary">
                <div class="badge">통합 운영</div>
                <div class="compare-label">통합 인프라 운영 시</div>
                <div class="compare-usd">{{ usd(integrated) }}<span class="per">/월</span></div>
            </div>
        </div>

        <div class="saving-banner">
            저희 서버에 통합 운영하면 월 <strong>{{ usd(saving) }}</strong> ·
            <strong>{{ pct(savingRate) }}</strong> 절감됩니다.
        </div>

        <!-- 비용에 포함되는 항목 -->
        <div class="settings-card">
            <h3 class="card-title">비용에 포함되는 항목</h3>
            <ul class="cost-items">
                <li>
                    <strong>서버 가동 (Compute)</strong>
                    웹사이트와 관리자 페이지의 24시간 상시 운영, 시공후기·문의하기 게시글 조회/작성,
                    포트폴리오 사진 제공 등 서버 연산 비용입니다.
                </li>
                <li>
                    <strong>데이터·이미지 저장 (스토리지)</strong>
                    시공후기·문의하기 게시글과 댓글, 업로드된 사진(첨부 이미지·포트폴리오 사진)을
                    보관하는 비용입니다.
                </li>
                <li>
                    <strong>데이터 백업 (백업 스토리지)</strong>
                    전체 데이터(게시글·이미지 등)를 별도 저장소에 안전하게 보관하는 백업 파일 비용입니다.
                </li>
                <li>
                    <strong>네트워크·고정 IP</strong>
                    데이터 전송과 고정 IP는 무료 제공 범위 내라 <strong>별도 비용이 발생하지 않습니다.</strong>
                </li>
            </ul>
        </div>

        <!-- 월별 서버 비용 (일할 계산) -->
        <div class="settings-card">
            <h3 class="card-title">월별 서버 비용</h3>
            <p class="desc">
                현재 월간 서버 비용 <strong>{{ usd(integrated) }}</strong>을 일 단가
                <strong>{{ dailyUsd }}</strong>로 환산해, 해당 월의 일수만큼 부과합니다.
                (월별 일수 차이로 금액이 달라지며, 진행 중인 월은 경과 일수만큼만 반영됩니다.)
            </p>
            <div class="table-scroll">
                <table class="cost-table">
                    <thead>
                        <tr>
                            <th class="row-head">월</th>
                            <th>상태</th>
                            <th>적용 일수</th>
                            <th>서버 비용</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in ledger" :key="row.key">
                            <th class="row-head">{{ monthLabel(row) }}</th>
                            <td>
                                <span :class="['status', row.isCurrent ? 'progress' : 'fixed']">
                                    {{ row.isCurrent ? '진행 중' : '확정' }}
                                </span>
                            </td>
                            <td>{{ row.daysCounted }} / {{ row.daysInMonth }}일</td>
                            <td class="usd">{{ usd(row.cost) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <th class="row-head" colspan="3">누적 합계 ({{ monthLabel(ledgerStart) }} ~ {{ monthLabel(now) }})</th>
                            <td class="usd">{{ usd(ledgerTotal) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'

// ─── 비용 모델 ──────────────────────────────────────────────
const HOURS_PER_MONTH = 730    // 월 평균 가동 시간
const DAYS_PER_YEAR = 365      // 1년 = 365일 = 8,760시간 = 12 × 730시간

// OCI 정가 (USD)
const RATE = {
    ocpuHr: 0.01,
    memGbHr: 0.0015,
    blockGbMo: 0.0255,
    blockVpuGbMo: 0.0017,
    objGbMo: 0.0255
}

const OBJ_GB = 5   // 오브젝트 스토리지(백업)

// 스펙 정의
const SPECS = {
    individual: { ocpu: 2, memGb: 12, blockGb: 100, blockVpu: 10 }, // 개별 서버 구성 (현 구성)
    integrated: { ocpu: 1, memGb: 6,  blockGb: 50,  blockVpu: 0 }   // 통합 인프라 운영 (다운사이징)
}

// 운영/원장 시작 시점
const LEDGER_START = { year: 2026, month: 5 }

// 월 비용 = 컴퓨팅 + 블록볼륨 + 오브젝트
function monthlyCost (spec) {
    const compute = (spec.ocpu * RATE.ocpuHr + spec.memGb * RATE.memGbHr) * HOURS_PER_MONTH
    const block = spec.blockGb * RATE.blockGbMo + spec.blockGb * spec.blockVpu * RATE.blockVpuGbMo
    const obj = OBJ_GB * RATE.objGbMo
    return compute + block + obj
}

function ymIndex (y, m) { return y * 12 + (m - 1) }
function daysInMonth (y, m) { return new Date(y, m, 0).getDate() }

@Component
export default class Costs extends Vue {
    ledgerStart = { year: LEDGER_START.year, month: LEDGER_START.month }
    currentTime = new Date()   // 반응형: 날짜가 바뀌면 화면이 자동 갱신됨
    dayTimer = null

    mounted () {
        // 5분마다 확인해 '날짜'가 바뀐 경우에만 갱신 → 탭을 켜둔 채 자정을 넘겨도 자동 반영
        this.dayTimer = setInterval(() => {
            const d = new Date()
            const c = this.currentTime
            if (d.getDate() !== c.getDate() || d.getMonth() !== c.getMonth() || d.getFullYear() !== c.getFullYear()) {
                this.currentTime = d
            }
        }, 5 * 60 * 1000)
    }

    beforeDestroy () {
        if (this.dayTimer) clearInterval(this.dayTimer)
    }

    get now () {
        const d = this.currentTime
        return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
    }

    get individual () { return monthlyCost(SPECS.individual) }   // 개별 서버 구성 시
    get integrated () { return monthlyCost(SPECS.integrated) }   // 통합 인프라 운영 시 (= 현재 월간 서버 비용)
    get saving () { return this.individual - this.integrated }
    get savingRate () { return this.saving / this.individual }

    // 일 단가 = 월비용을 연 환산 후 365일로 나눔 (월별 일수에 따라 비용이 달라짐)
    get dailyRate () { return this.integrated * 12 / DAYS_PER_YEAR }
    get dailyUsd () { return '$' + this.dailyRate.toFixed(4) }

    // 월별 일할 계산 (고정 일 단가 × 적용 일수)
    get ledger () {
        const rows = []
        const end = ymIndex(this.now.year, this.now.month)
        let idx = ymIndex(LEDGER_START.year, LEDGER_START.month)
        while (idx <= end) {
            const year = Math.floor(idx / 12)
            const month = (idx % 12) + 1
            const dim = daysInMonth(year, month)
            const isCurrent = idx === end
            const daysCounted = isCurrent ? this.now.day : dim
            rows.push({
                key: `${year}-${month}`,
                year,
                month,
                isCurrent,
                daysInMonth: dim,
                daysCounted,
                cost: this.dailyRate * daysCounted
            })
            idx++
        }
        return rows
    }

    get ledgerTotal () {
        return this.ledger.reduce((sum, r) => sum + r.cost, 0)
    }

    // ─── 포매터 ───
    usd (n) { return '$' + n.toFixed(2) }
    pct (n) { return Math.round(n * 100) + '%' }
    monthLabel (ym) { return `${ym.year}.${String(ym.month).padStart(2, '0')}` }
}
</script>

<style scoped lang="scss">
@import 'src/assets/style/common';
@import 'src/assets/style/media-query';

.costs-container {
    padding: 20px 0 40px;
}
.page-title {
    color: $dark-theme;
    font-size: 24px;
    margin-bottom: 8px;
    text-align: left;
}
.page-sub {
    color: #666;
    font-size: 13px;
    line-height: 1.6;
    margin: 0 0 24px;
}

.compare {
    display: flex;
    align-items: stretch;
    gap: 14px;
    margin-bottom: 16px;

    @include mobile {
        flex-direction: column;
    }
}
.compare-card {
    flex: 1;
    position: relative;
    background: #ffffff;
    border: 1px solid $transparent-dark-theme;
    border-radius: 10px;
    padding: 22px 24px;
    text-align: center;

    &.primary {
        background: $dark-theme;
        border-color: $dark-theme;
        .compare-label { color: rgba(255, 255, 255, 0.85); }
        .compare-usd { color: $bright-theme; }
    }
    .badge {
        position: absolute;
        top: 10px;
        right: 10px;
        background: #2e7d32;
        color: #fff;
        font-size: 10px;
        font-weight: bold;
        padding: 2px 8px;
        border-radius: 8px;
    }
    .compare-label {
        color: #888;
        font-size: 13px;
        margin-bottom: 10px;
    }
    .compare-usd {
        color: $dark-theme;
        font-size: 30px;
        font-weight: bold;
        .per { font-size: 14px; font-weight: normal; opacity: 0.7; margin-left: 2px; }
    }
}
.compare-arrow {
    display: flex;
    align-items: center;
    color: $dark-theme-100;
    font-size: 22px;

    @include mobile {
        justify-content: center;
        transform: rotate(90deg);
    }
}

.saving-banner {
    background: #eef6ee;
    border: 1px solid #cfe5cf;
    color: #2e7d32;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
    margin-bottom: 24px;
    strong { font-weight: bold; }
}

.settings-card {
    background: #ffffff;
    border: 1px solid $transparent-dark-theme;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

    .card-title {
        color: $dark-theme;
        font-size: 18px;
        margin: 0 0 16px;
        padding-bottom: 10px;
        border-bottom: 1px solid $transparent-dark-theme;
    }
    .desc {
        color: #666;
        font-size: 13px;
        line-height: 1.6;
        margin: -4px 0 18px;
        strong { color: $dark-theme; }
    }
}

.cost-items {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        position: relative;
        padding: 0 0 14px 14px;
        font-size: 13px;
        line-height: 1.65;
        color: #666;

        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 7px;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: $dark-theme-100;
        }
        &:last-child { padding-bottom: 0; }

        strong {
            display: block;
            color: $dark-theme;
            font-size: 14px;
            margin-bottom: 2px;
            &:not(:first-child) { display: inline; font-size: 13px; }
        }
    }
}

.table-scroll {
    width: 100%;
    overflow-x: auto;
}
.cost-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    color: $dark-theme;

    th, td {
        border: 1px solid $transparent-dark-theme;
        padding: 12px 14px;
        text-align: center;
        vertical-align: middle;
    }
    thead th {
        background: $bright-theme-100;
        font-weight: bold;
        white-space: nowrap;
    }
    .row-head {
        background: $bright-theme-100;
        font-weight: bold;
        text-align: left;
        white-space: nowrap;
    }
    td.usd { font-size: 16px; font-weight: bold; }

    tfoot td, tfoot th {
        background: $bright-theme-100;
        font-weight: bold;
    }

    .status {
        display: inline-block;
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 10px;
        &.fixed { background: #eef0f0; color: #555; }
        &.progress { background: #fff3cd; color: #8a6d00; }
    }
}

@include mobile {
    .cost-table { font-size: 13px; }
    .cost-table th, .cost-table td { padding: 9px 8px; }
}
</style>
